import useSWRImmutable from 'swr/immutable';

import { SavedItemModel } from '@/lib/models/saved-item/types';
import { SavedShipmentViewsApiResponse } from '@/types/api-types';
import { request } from '@/utils/api/request';
import { getSavedShipmentViewsUrl } from '@/utils/helpers/api-urls';
import { trackUser } from '@/utils/tracking';

const ERROR_MESSAGE = 'Error fetching saved items';

const DEFAULT_OPTIMISTIC_ARGS = {
  rollbackOnError: true,
  populateCache: true,
  revalidate: true,
};

const LOGGING_ID = 'SAVED_SHIPMENT_VIEW';

export const useSavedShipmentViews = () => {
  const { data, error, mutate, isLoading } = useSWRImmutable<SavedShipmentViewsApiResponse>(
    getSavedShipmentViewsUrl(),
    request,
  );

  const onAdd = async (savedItem: SavedItemModel) => {
    const result = await request(getSavedShipmentViewsUrl(), { body: savedItem }, 'POST');
    if (result) {
      trackUser.event(`${LOGGING_ID}: Created`);
    }
    return { savedShipmentViews: [...(data?.savedShipmentViews || []), savedItem] };
  };

  const onUpdate = async (savedItem: SavedItemModel) => {
    const result = await request(getSavedShipmentViewsUrl(savedItem.id), { body: savedItem }, 'PATCH');
    if (result) {
      trackUser.event(`${LOGGING_ID}: Updated`);
    }
    return { savedShipmentViews: [...(data?.savedShipmentViews || []), savedItem] };
  };

  const onDelete = async (savedItem: SavedItemModel) => {
    const result = await request(getSavedShipmentViewsUrl(savedItem.id), undefined, 'DELETE');
    if (result) {
      trackUser.event(`${LOGGING_ID}: Deleted`);
    }
    return { savedShipmentViews: data?.savedShipmentViews?.filter((item) => item.id !== savedItem.id) || [] };
  };

  const handleCreate = async (savedItem: SavedItemModel) => {
    return mutate(onAdd(savedItem), {
      optimisticData: { savedShipmentViews: [...(data?.savedShipmentViews || []), savedItem] },
      ...DEFAULT_OPTIMISTIC_ARGS,
    });
  };

  const handleUpdate = async (savedItem: SavedItemModel) => {
    await onUpdate(savedItem);
    return mutate();
  };

  const handleDelete = async (savedItem: SavedItemModel) => {
    return mutate(onDelete(savedItem), {
      optimisticData: {
        savedShipmentViews: data?.savedShipmentViews?.filter((item) => item.id !== savedItem.id) || [],
      },
      ...DEFAULT_OPTIMISTIC_ARGS,
    });
  };

  const errorMessage = error ? ERROR_MESSAGE : undefined;

  return {
    isLoading,
    savedShipmentViews: data?.savedShipmentViews || [],
    error: errorMessage,
    create: handleCreate,
    del: handleDelete,
    update: handleUpdate,
  };
};
